import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { authMe, authLogout, adminMetrics, adminRegistrations, adminUpdateReg, adminWaitlist, adminGetSettings, adminUpdateSettings, adminCheckin, API } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Download, Search, ShieldCheck, XCircle, CheckCircle2, Users, DollarSign, Clock, Trophy, UserCheck, Settings2, Loader2, ArrowLeft, Wallet, FileText, ListChecks, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("registrations");
  const [metrics, setMetrics] = useState(null);
  const [regs, setRegs] = useState([]);
  const [q, setQ] = useState("");
  const [pStatus, setPStatus] = useState("all");
  const [rStatus, setRStatus] = useState("all");
  const [rType, setRType] = useState("all");
  const [selected, setSelected] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authMe().then(u => {
      if (!u || !u.is_admin) { nav("/admin"); return; }
      setUser(u); refreshAll();
    }).catch(() => nav("/admin"));
  }, []);

  async function refreshAll() {
    try {
      const [m, r, w, s] = await Promise.all([adminMetrics(), adminRegistrations({}), adminWaitlist(), adminGetSettings()]);
      setMetrics(m); setRegs(r); setWaitlist(w); setSettings(s);
    } catch(e) { console.error(e); }
  }

  const filtered = useMemo(() => {
    return regs.filter(r => {
      if (pStatus !== "all" && r.payment_status !== pStatus) return false;
      if (rStatus !== "all" && r.registration_status !== rStatus) return false;
      if (rType !== "all" && r.registration_type !== rType) return false;
      if (q) {
        const t = q.toLowerCase();
        return [r.full_name, r.email, r.mobile_number, r.registration_number, r.gcash_reference].some(x => (x||"").toLowerCase().includes(t));
      }
      return true;
    });
  }, [regs, q, pStatus, rStatus, rType]);

  async function updateReg(id, patch, msg="Updated") {
    try {
      const updated = await adminUpdateReg(id, patch);
      toast.success(msg);
      setRegs(prev => prev.map(r => r.id === id ? updated : r));
      if (selected?.id === id) setSelected(updated);
      const m = await adminMetrics(); setMetrics(m);
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function logout() { await authLogout(); nav("/admin"); }

  function exportCsv() {
    window.open(`${API}/admin/export.csv`, "_blank");
  }

  async function saveSettings() {
    setSaving(true);
    try {
      const s = await adminUpdateSettings(settings);
      setSettings(s);
      toast.success("Settings saved");
      const m = await adminMetrics(); setMetrics(m);
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  if (!user || !metrics) {
    return <div className="min-h-screen bg-[#06080F] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300"/></div>;
  }

  return (
    <div className="min-h-screen bg-[#06080F] text-white">
      <header className="border-b border-white/5 bg-[#080C18]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_build-framework-27/artifacts/xu1ebv9g_MOOLAH%20LOGO.jpg" alt="Moolah Tech" className="h-8 w-8 rounded-lg object-cover shadow-[0_0_16px_rgba(56,189,248,0.3)]"/>
            <span className="font-bold tracking-tight">MOOLAH <span className="text-cyan-400">TECH</span> <span className="text-slate-500 font-normal ml-1">/ ADMIN</span></span>
          </a>
          <div className="flex items-center gap-3">
            <button onClick={refreshAll} className="text-slate-400 hover:text-cyan-300 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5"/> Refresh</button>
            <div className="text-xs text-slate-400 hidden sm:block">{user.email}</div>
            <button onClick={logout} className="text-slate-400 hover:text-red-400 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid="admin-logout-btn"><LogOut className="w-3.5 h-3.5"/> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
        {/* METRICS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <MetricCard testid="admin-stat-total-registrations" icon={<Users className="w-4 h-4"/>} label="Total Registrations" value={metrics.total_registrations}/>
          <MetricCard icon={<Clock className="w-4 h-4"/>} label="Pending Verification" value={metrics.pending_verification} tone="amber"/>
          <MetricCard icon={<CheckCircle2 className="w-4 h-4"/>} label="Confirmed" value={metrics.confirmed_participants} tone="emerald"/>
          <MetricCard testid="admin-stat-revenue" icon={<DollarSign className="w-4 h-4"/>} label="Verified Revenue" value={`₱${metrics.total_verified_revenue.toLocaleString()}`} tone="cyan"/>
          <MetricCard icon={<Trophy className="w-4 h-4"/>} label="Seats Remaining" value={metrics.seats_remaining} tone="cyan"/>
          <MetricCard icon={<Wallet className="w-4 h-4"/>} label="Early Bird" value={metrics.early_bird_count}/>
          <MetricCard icon={<Wallet className="w-4 h-4"/>} label="Regular" value={metrics.regular_count}/>
          <MetricCard icon={<ShieldCheck className="w-4 h-4"/>} label="Verified Payments" value={metrics.verified_payments} tone="emerald"/>
          <MetricCard icon={<ListChecks className="w-4 h-4"/>} label="Waitlisted" value={metrics.waitlisted}/>
          <MetricCard icon={<UserCheck className="w-4 h-4"/>} label="Attended" value={metrics.attended}/>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-white/[0.03] border border-white/5">
            <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Event Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="registrations" className="mt-5">
            <div className="glass p-4 sm:p-5">
              <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
                <div className="flex-1 flex items-center gap-2 bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                  <Search className="w-4 h-4 text-slate-500"/>
                  <input data-testid="admin-search-input" placeholder="Search name, email, phone, reg #, GCash ref…" value={q} onChange={e=>setQ(e.target.value)} className="bg-transparent flex-1 outline-none text-sm"/>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Filter label="Payment" value={pStatus} onChange={setPStatus} testid="admin-filter-status" options={["all","Pending Verification","Verified","Rejected"]}/>
                  <Filter label="Registration" value={rStatus} onChange={setRStatus} options={["all","Pending Verification","Confirmed","Rejected","Cancelled","Waitlisted","Attended","No Show"]}/>
                  <Filter label="Type" value={rType} onChange={setRType} options={["all","early_bird","regular"]}/>
                  <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="cta-outline text-xs inline-flex items-center gap-2"><Download className="w-3.5 h-3.5"/> Export CSV</button>
                </div>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/5">
                    <tr>
                      {["Reg #","Name","Email","Mobile","Type","Amount","Payment","Registration","Submitted",""].map(h => <th key={h} className="text-left py-3 px-3 font-normal">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`row-${r.registration_number}`}>
                        <td className="py-3 px-3 font-mono text-cyan-300 text-xs">{r.registration_number}</td>
                        <td className="py-3 px-3">{r.full_name}{r.duplicate_flag && <span className="ml-2 text-[10px] text-amber-300 font-mono">DUP?</span>}</td>
                        <td className="py-3 px-3 text-slate-400">{r.email}</td>
                        <td className="py-3 px-3 text-slate-400">{r.mobile_number}</td>
                        <td className="py-3 px-3">{r.registration_type === "early_bird" ? "Early" : "Regular"}</td>
                        <td className="py-3 px-3 font-mono">₱{r.amount_due}</td>
                        <td className="py-3 px-3"><StatusPill status={r.payment_status}/></td>
                        <td className="py-3 px-3"><StatusPill status={r.registration_status}/></td>
                        <td className="py-3 px-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-3">
                          <button onClick={()=>setSelected(r)} className="text-cyan-300 hover:text-cyan-200 text-xs font-mono uppercase tracking-widest" data-testid={`view-${r.registration_number}`}>Open</button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={10} className="py-10 text-center text-slate-500">No registrations found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="waitlist" className="mt-5">
            <div className="glass p-5">
              <div className="text-lg font-semibold mb-3">Waitlist ({waitlist.length})</div>
              <table className="w-full text-sm">
                <thead className="text-[11px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/5">
                  <tr>{["Name","Email","Mobile","Type","Joined"].map(h=><th key={h} className="text-left py-3 px-3 font-normal">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {waitlist.map(w => (
                    <tr key={w.id} className="border-b border-white/5">
                      <td className="py-3 px-3">{w.full_name}</td>
                      <td className="py-3 px-3 text-slate-400">{w.email}</td>
                      <td className="py-3 px-3 text-slate-400">{w.mobile_number}</td>
                      <td className="py-3 px-3">{w.participant_type}</td>
                      <td className="py-3 px-3 text-slate-500 text-xs">{new Date(w.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {waitlist.length===0 && <tr><td colSpan={5} className="py-10 text-center text-slate-500">No one on the waitlist yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="attendance" className="mt-5">
            <div className="glass p-5">
              <div className="text-lg font-semibold mb-2">Check-In</div>
              <p className="text-slate-500 text-xs mb-4">Search a confirmed participant and check them in.</p>
              <div className="flex items-center gap-2 bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2 mb-4">
                <Search className="w-4 h-4 text-slate-500"/>
                <input placeholder="Search name / reg # / mobile" value={q} onChange={e=>setQ(e.target.value)} className="bg-transparent flex-1 outline-none text-sm"/>
              </div>
              <div className="space-y-2">
                {filtered.filter(r=>r.registration_status==="Confirmed" || r.registration_status==="Attended").slice(0,50).map(r => (
                  <div key={r.id} className="flex items-center justify-between glass px-4 py-3">
                    <div>
                      <div className="font-medium">{r.full_name}</div>
                      <div className="text-slate-500 text-xs font-mono">{r.registration_number} · {r.mobile_number}</div>
                    </div>
                    {r.registration_status === "Attended" ? (
                      <div className="chip text-emerald-300 border-emerald-400/30 bg-emerald-400/10"><CheckCircle2 className="w-3 h-3"/> Attended</div>
                    ) : (
                      <button data-testid={`checkin-${r.registration_number}`} onClick={()=>adminCheckin(r.id).then(()=>{ toast.success("Checked in"); refreshAll(); })} className="cta-primary text-xs">CHECK IN</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            {settings && (
              <div className="glass p-5">
                <div className="text-lg font-semibold mb-4">Event Settings</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <F label="Event Name"><Input value={settings.event_name} onChange={e=>setSettings({...settings, event_name:e.target.value})}/></F>
                  <F label="Date (YYYY-MM-DD)"><Input value={settings.event_date} onChange={e=>setSettings({...settings, event_date:e.target.value})}/></F>
                  <F label="Start Time (HH:MM)"><Input value={settings.start_time} onChange={e=>setSettings({...settings, start_time:e.target.value})}/></F>
                  <F label="End Time (HH:MM)"><Input value={settings.end_time} onChange={e=>setSettings({...settings, end_time:e.target.value})}/></F>
                  <F label="Venue Name"><Input value={settings.venue_name} onChange={e=>setSettings({...settings, venue_name:e.target.value})}/></F>
                  <F label="Venue Floor / Room"><Input value={settings.venue_floor} onChange={e=>setSettings({...settings, venue_floor:e.target.value})}/></F>
                  <F label="Venue Address"><Input value={settings.venue_address} onChange={e=>setSettings({...settings, venue_address:e.target.value})}/></F>
                  <F label="Capacity"><Input type="number" value={settings.capacity} onChange={e=>setSettings({...settings, capacity:Number(e.target.value)})}/></F>
                  <F label="Early Bird Price"><Input type="number" value={settings.early_bird_price} onChange={e=>setSettings({...settings, early_bird_price:Number(e.target.value)})}/></F>
                  <F label="Regular Price"><Input type="number" value={settings.regular_price} onChange={e=>setSettings({...settings, regular_price:Number(e.target.value)})}/></F>
                  <F label="Early Bird Deadline (ISO)"><Input value={settings.early_bird_deadline} onChange={e=>setSettings({...settings, early_bird_deadline:e.target.value})}/></F>
                  <F label="GCash Number"><Input value={settings.gcash_number} onChange={e=>setSettings({...settings, gcash_number:e.target.value})}/></F>
                  <F label="GCash QR URL (or upload below)"><Input value={settings.gcash_qr_url} onChange={e=>setSettings({...settings, gcash_qr_url:e.target.value})}/></F>
                  <F label="Contact Email"><Input value={settings.contact_email} onChange={e=>setSettings({...settings, contact_email:e.target.value})}/></F>
                  <F label="Contact Number"><Input value={settings.contact_number} onChange={e=>setSettings({...settings, contact_number:e.target.value})}/></F>
                  <F label="Video URL"><Input value={settings.video_url || ""} onChange={e=>setSettings({...settings, video_url:e.target.value})}/></F>
                </div>
                <div className="flex items-center gap-4 mt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={settings.registration_open} onChange={e=>setSettings({...settings, registration_open:e.target.checked})}/>
                    Registration Open
                  </label>
                  <button onClick={saveSettings} data-testid="admin-save-settings" className="cta-primary text-sm inline-flex items-center gap-2">
                    {saving && <Loader2 className="w-4 h-4 animate-spin"/>}Save Settings
                  </button>
                </div>
                <div className="mt-6">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Upload GCash QR</div>
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={async e=>{
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const res = await fetch(`${API}/admin/gcash-qr`, { method:"POST", credentials:"include", body: fd });
                      const j = await res.json();
                      if (j.url) { setSettings(s => ({...s, gcash_qr_url: j.url})); toast.success("QR uploaded"); }
                    } catch { toast.error("Upload failed"); }
                  }}/>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* DETAIL MODAL */}
      <Dialog open={!!selected} onOpenChange={o=>!o && setSelected(null)}>
        <DialogContent className="max-w-4xl bg-[#080C18] border-cyan-500/20 text-white">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-3">
                  <span className="font-mono text-cyan-300">{selected.registration_number}</span>
                  <span>{selected.full_name}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-5 mt-2">
                <div className="space-y-3">
                  <Info label="Email" value={selected.email}/>
                  <Info label="Mobile" value={selected.mobile_number}/>
                  <Info label="Participant Type" value={selected.participant_type}/>
                  <Info label="Project Idea" value={selected.project_idea || "—"}/>
                  <Info label="Registration Type" value={selected.registration_type === "early_bird" ? "Early Bird" : "Regular"}/>
                  <Info label="Amount Due" value={`₱${selected.amount_due}`}/>
                  <Info label="GCash Reference" value={<span className="font-mono">{selected.gcash_reference}</span>}/>
                  <Info label="Payment Status" value={<StatusPill status={selected.payment_status}/>}/>
                  <Info label="Registration Status" value={<StatusPill status={selected.registration_status}/>}/>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Admin Notes</div>
                    <Textarea rows={3} defaultValue={selected.admin_notes || ""} onBlur={e=>updateReg(selected.id, { admin_notes: e.target.value }, "Notes saved")}/>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Payment Proof</div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2">
                    <ProofViewer url={selected.payment_proof_url}/>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-white/5">
                <button onClick={()=>updateReg(selected.id, { payment_status:"Verified" }, "Payment verified")} data-testid="admin-verify-payment-btn" className="cta-primary text-xs inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5"/> Verify Payment</button>
                <button onClick={()=>updateReg(selected.id, { payment_status:"Rejected" }, "Payment rejected")} className="cta-outline text-xs inline-flex items-center gap-2 text-red-300 border-red-400/30 hover:bg-red-500/10"><XCircle className="w-3.5 h-3.5"/> Reject Payment</button>
                <button onClick={()=>updateReg(selected.id, { registration_status:"Confirmed" }, "Confirmed")} className="cta-outline text-xs">Confirm Registration</button>
                <button onClick={()=>updateReg(selected.id, { registration_status:"Cancelled" })} className="cta-outline text-xs">Cancel</button>
                <button onClick={()=>updateReg(selected.id, { registration_status:"Waitlisted" })} className="cta-outline text-xs">Move to Waitlist</button>
                <button onClick={()=>adminCheckin(selected.id).then(()=>{ toast.success("Attended"); refreshAll(); setSelected({...selected, registration_status:"Attended"}); })} className="cta-outline text-xs">Mark Attended</button>
                <button onClick={()=>updateReg(selected.id, { registration_status:"No Show" })} className="cta-outline text-xs">Mark No Show</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({icon,label,value,tone,testid}) {
  const tones = { cyan:"text-cyan-300 border-cyan-400/30 bg-cyan-400/[0.04]", emerald:"text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.04]", amber:"text-amber-300 border-amber-400/30 bg-amber-400/[0.04]" };
  return (
    <div data-testid={testid} className="glass p-4">
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${tones[tone] || "text-slate-300 border-white/10 bg-white/[0.03]"}`}>{icon}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-3">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function Filter({label, value, onChange, options, testid}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-9 bg-white/[0.03] border-white/10 text-xs w-[160px]"><SelectValue/></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o==="all"?"All":o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusPill({status}) {
  const map = {
    "Pending Verification": "text-amber-300 border-amber-400/30 bg-amber-400/10",
    "Verified": "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    "Confirmed": "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    "Rejected": "text-red-300 border-red-400/30 bg-red-400/10",
    "Cancelled": "text-slate-400 border-slate-400/30 bg-slate-400/10",
    "Waitlisted": "text-cyan-300 border-cyan-400/30 bg-cyan-400/10",
    "Attended": "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    "No Show": "text-slate-500 border-slate-500/30 bg-slate-500/10",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border ${map[status] || "text-slate-400 border-white/10"}`}>{status}</span>;
}

function Info({label,value}) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-200">{value}</div>
    </div>
  );
}

function F({label, children}) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function ProofViewer({url}) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let obj = null;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, { credentials: "include" });
        if (!res.ok) { setErr(true); return; }
        const blob = await res.blob();
        obj = URL.createObjectURL(blob);
        setSrc(obj);
      } catch { setErr(true); }
    })();
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (err) return <div className="text-slate-500 text-sm p-4">Unable to load proof.</div>;
  if (!src) return <div className="text-slate-500 text-sm p-4">Loading…</div>;
  if (url.endsWith(".pdf")) return <iframe src={src} title="proof" className="w-full h-[520px] rounded-lg bg-white"/>;
  return <img src={src} alt="Payment proof" className="w-full max-h-[520px] object-contain rounded-lg"/>;
}
